import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHome, useProfile } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { MascotWatermark } from '../components/Watermark';
import { WaveIcon } from '../components/Wave';
import { haptics, isTelegram, openTelegramLink, tg, tgVersionAtLeast } from '../telegram';
import { track } from '../analytics';
import { money, seasonLabel, shortDate, timeLabel } from '../format';
import type { NewsItem } from '../api/types';

/** Кнопка «на главный экран»: только в Telegram ≥ 8.0 и пока ярлыка ещё нет. */
function useHomeScreenShortcut() {
  const [available, setAvailable] = useState(false);

  const check = useCallback(() => {
    if (!isTelegram || !tgVersionAtLeast('8.0') || !tg?.checkHomeScreenStatus) return;
    try {
      tg.checkHomeScreenStatus((status) => setAvailable(status === 'missed'));
    } catch {
      /* старый клиент — кнопку не показываем */
    }
  }, []);

  useEffect(check, [check]);

  const add = useCallback(() => {
    haptics.tap();
    track('add_to_home_click');
    try {
      tg?.addToHomeScreen?.();
    } catch {
      /* no-op */
    }
    // Системный диалог асинхронный: перепроверяем статус — если ярлык
    // добавили, кнопка исчезнет; если отказались — останется.
    setTimeout(check, 3000);
  }, [check]);

  return { available, add };
}

function NewsRow({ n }: { n: NewsItem }) {
  return (
    <div className="news-item">
      <WaveIcon />
      <div>
        <div className="ttl">{n.title}</div>
        {n.body && <div className="bd">{n.body}</div>}
      </div>
    </div>
  );
}

export function HomeScreen() {
  const { player } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useHome();
  // Шапка мокапа показывает сезон/очки/место — берём из профиля (кэшируется).
  const profile = useProfile(null);
  const shortcut = useHomeScreenShortcut();

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const t = data.nextTournament;
  const live = t?.status === 'in_progress';
  const meta = t
    ? [shortDate(t.startTime), timeLabel(t.startTime), t.format].filter(Boolean).join(' · ')
    : '';
  const seatsFree =
    t && t.capacity && t.capacity > 0
      ? Math.max(0, t.capacity - (t.registeredCount - (t.bustedCount ?? 0)))
      : null;
  const stats = profile.data?.stats;

  return (
    <div>
      <header className="bhead home-head">
        <MascotWatermark pos="tr" />
        <div className="top">
          <img className="brandmark" src="/ante-white.svg" alt="ANTÉ" />
          <Link to="/profile">
            <Avatar name={player?.displayName} src={player?.photoUrl} size={38} bordered />
          </Link>
        </div>
        <div className="hello">Привет,</div>
        <div className="who">{player?.displayName ?? 'Игрок'}</div>
        {stats && (
          <div className="season">
            {profile.data?.seasonId ? `${seasonLabel(profile.data.seasonId)} · ` : ''}
            <b>{stats.points}</b> очков{stats.rank ? <> · <b>#{stats.rank}</b></> : null}
          </div>
        )}
      </header>

      <div className="curtain">
        <div className="date-group date-group--accent" style={{ marginTop: 0 }}>
          {live ? 'Сейчас идёт' : 'Ближайший турнир'}
        </div>
        {t ? (
          // Вся карточка — одна кликабельная зона (нижняя кнопка-Link гасит
          // всплытие, чтобы «Смотреть Live» не давал двойную навигацию).
          <div
            className="featured"
            role="button"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/tournaments/${t.id}`)}
          >
            <div className="name">{t.title ?? t.format ?? 'Турнир'}</div>
            <div className="meta">{meta}</div>
            <div className="metrics">
              <div className="metric">
                {t.buyIn > 0 ? (
                  <div className="v">
                    {t.buyIn.toLocaleString('ru-RU')} <span className="unit">₽</span>
                  </div>
                ) : (
                  <div className="v">Free</div>
                )}
                <div className="l">Бай-ин</div>
              </div>
              {seatsFree !== null ? (
                <div className="metric">
                  <div className="v plain">
                    {seatsFree}
                    <span className="unit">/{t.capacity}</span>
                  </div>
                  <div className="l">Свободно</div>
                </div>
              ) : (
                <div className="metric">
                  <div className="v plain">{t.registeredCount}</div>
                  <div className="l">Записались</div>
                </div>
              )}
            </div>
            {live ? (
              <>
                <Link
                  to={`/tournaments/${t.id}/live`}
                  className="btn"
                  style={{ display: 'block', textAlign: 'center' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    track('live_click', { gameId: t.id, from: 'home' });
                  }}
                >
                  ● Смотреть Live
                </Link>
                {/* Идёт, но поздняя регистрация ещё открыта (registrationOpen
                    наложен сервером из live-данных Korin) — дать путь к записи. */}
                {t.registrationOpen && !t.registeredByMe && (
                  <Link
                    to={`/tournaments/${t.id}`}
                    className="btn btn--ghost"
                    style={{ display: 'block', textAlign: 'center', marginTop: 8 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Записаться{t.buyIn > 0 ? ` · ${money(t.buyIn)}` : ''}
                  </Link>
                )}
              </>
            ) : (
              <Link
                to={`/tournaments/${t.id}`}
                className="btn"
                style={{ display: 'block', textAlign: 'center' }}
                onClick={(e) => e.stopPropagation()}
              >
                {t.registeredByMe
                  ? 'Вы записаны'
                  : `Записаться${t.buyIn > 0 ? ` · ${money(t.buyIn)}` : ''}`}
              </Link>
            )}
          </div>
        ) : (
          <div className="card muted" style={{ marginBottom: 22 }}>
            Ближайших турниров пока нет
          </div>
        )}

        <div className="section-header">
          <h2>Новости клуба</h2>
        </div>
        {data.news.length === 0 ? (
          <div className="muted tiny">Пока нет новостей</div>
        ) : (
          data.news.map((n) => <NewsRow key={n.id} n={n} />)
        )}

        {shortcut.available && (
          <button
            className="btn btn--ghost"
            style={{ display: 'block', width: '100%', textAlign: 'center', margin: '22px 0 0' }}
            onClick={shortcut.add}
          >
            📲 Добавить приложение на главный экран
          </button>
        )}

        <button
          className="btn btn--ghost"
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'center',
            margin: shortcut.available ? '4px 0 4px' : '22px 0 4px',
          }}
          onClick={() => {
            haptics.tap();
            track('help_click');
            openTelegramLink('https://telegram.me/ante_assistant');
          }}
        >
          💬 Нужна помощь? Напишите нам
        </button>
      </div>
    </div>
  );
}
